/**
 * Skill Marketplace API Test Panel — for ApiTestPage.
 *
 * Tests all 6 Smithy TS Client operations via SigV4 auth:
 *   Federate id_token → Cognito Identity Pool → AWS Credentials → SigV4 → API Gateway → Lambda
 */

import { useState, useCallback } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Input,
  Button,
  Box,
  StatusIndicator,
  Select,
  type SelectProps,
  ColumnLayout,
  Alert,
} from '@amzn/awsui-components-react';
import { useAuth } from '../context/AuthContext';
import {
  getSkillClient,
  ListCatalogCommand,
  GetCatalogItemCommand,
  ListEquipmentCommand,
  EquipSkillCommand,
  UnequipSkillCommand,
} from '../client/SkillClient';
import { appConfig } from '../config';

// ── Operation definitions ───────────────────────────────────────────────────

interface OpField {
  key: string;
  label: string;
  placeholder: string;
  required?: boolean;
  defaultValue?: string;
}

interface OpConfig {
  label: string;
  description: string;
  fields: OpField[];
}

const OPERATIONS: Record<string, OpConfig> = {
  listCatalog: {
    label: 'List Catalog',
    description: 'List all skills in the marketplace',
    fields: [
      { key: 'category', label: 'Category', placeholder: '(optional) e.g., Revenue' },
      { key: 'agentName', label: 'Agent Name', placeholder: '(optional) e.g., data_ops_agent' },
      { key: 'subCategory', label: 'Sub-Category', placeholder: '(optional)' },
      { key: 'maxResults', label: 'Max Results', placeholder: '(optional) e.g., 10' },
    ],
  },
  getCatalogItem: {
    label: 'Get Catalog Item',
    description: 'Get details for a single skill',
    fields: [
      { key: 'skillName', label: 'Skill Name', placeholder: 'e.g., weekly-revenue-report', required: true },
    ],
  },
  listEquipment: {
    label: 'List Equipment',
    description: 'List skills installed by a user',
    fields: [
      { key: 'agentName', label: 'Agent Name', placeholder: '(optional) e.g., data_ops_agent' },
    ],
  },
  equipSkill: {
    label: 'Equip Skill',
    description: 'Install a skill for the current user',
    fields: [
      { key: 'agentName', label: 'Agent Name', placeholder: 'e.g., data_ops_agent', required: true },
      { key: 'skillName', label: 'Skill Name', placeholder: 'e.g., weekly-revenue-report', required: true },
    ],
  },
  unequipSkill: {
    label: 'Unequip Skill',
    description: 'Uninstall a skill for the current user',
    fields: [
      { key: 'agentName', label: 'Agent Name', placeholder: 'e.g., data_ops_agent', required: true },
      { key: 'skillName', label: 'Skill Name', placeholder: 'e.g., weekly-revenue-report', required: true },
    ],
  },
};

// ── Component ───────────────────────────────────────────────────────────────

export default function SkillMarketplaceTester() {
  const { uid } = useAuth();
  const [selectedOp, setSelectedOp] = useState<SelectProps.Option>({
    label: OPERATIONS.listCatalog.label,
    value: 'listCatalog',
  });
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [elapsed, setElapsed] = useState<number | null>(null);

  const opKey = selectedOp.value ?? 'listCatalog';
  const opConfig = OPERATIONS[opKey];

  const handleOpChange = useCallback((detail: { selectedOption: SelectProps.Option }) => {
    setSelectedOp(detail.selectedOption);
    setFieldValues({});
    setResult('');
    setError('');
    setElapsed(null);
  }, []);

  const handleFieldChange = useCallback((key: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleExecute = useCallback(async () => {
    setLoading(true);
    setResult('');
    setError('');
    setElapsed(null);

    const t0 = performance.now();

    try {
      const client = getSkillClient();
      let response: unknown;

      switch (opKey) {
        case 'listCatalog':
          response = await client.send(
            new ListCatalogCommand({
              category: fieldValues.category || undefined,
              agentName: fieldValues.agentName || undefined,
              subCategory: fieldValues.subCategory || undefined,
              maxResults: fieldValues.maxResults ? parseInt(fieldValues.maxResults, 10) : undefined,
            }),
          );
          break;

        case 'getCatalogItem':
          response = await client.send(
            new GetCatalogItemCommand({ skillName: fieldValues.skillName || '' }),
          );
          break;

        case 'listEquipment':
          response = await client.send(
            new ListEquipmentCommand({
              userAlias: uid,
              agentName: fieldValues.agentName || undefined,
            }),
          );
          break;

        case 'equipSkill':
          response = await client.send(
            new EquipSkillCommand({
              userAlias: uid,
              agentName: fieldValues.agentName || '',
              skillName: fieldValues.skillName || '',
            }),
          );
          break;

        case 'unequipSkill':
          response = await client.send(
            new UnequipSkillCommand({
              userAlias: uid,
              agentName: fieldValues.agentName || '',
              skillName: fieldValues.skillName || '',
            }),
          );
          break;

        default:
          throw new Error(`Unknown operation: ${opKey}`);
      }

      const t1 = performance.now();
      setElapsed(Math.round(t1 - t0));
      setResult(JSON.stringify(response, null, 2));
    } catch (err) {
      const t1 = performance.now();
      setElapsed(Math.round(t1 - t0));
      setError(err instanceof Error ? `${err.name}: ${err.message}` : String(err));
    } finally {
      setLoading(false);
    }
  }, [opKey, fieldValues, uid]);

  return (
    <SpaceBetween size="l">
      {/* Auth info banner */}
      <Alert type="info">
        <strong>SigV4 Auth Chain:</strong> Federate id_token → Cognito Identity Pool (
        <code>{appConfig.identityPoolId}</code>) → AWS Credentials → SigV4 signed request.
        <br />
        <strong>Service API:</strong> <code>{appConfig.skillApiEndpoint}</code>
        <br />
        <strong>User:</strong> {uid || '(not authenticated)'} | <strong>Stage:</strong>{' '}
        {appConfig.stage} | <strong>Region:</strong> {appConfig.region}
      </Alert>

      {/* Operation selector + fields */}
      <Container header={<Header variant="h2">Skill Marketplace API</Header>}>
        <SpaceBetween size="m">
          <FormField label="Operation">
            <Select
              selectedOption={selectedOp}
              onChange={({ detail }) => handleOpChange(detail)}
              options={Object.entries(OPERATIONS).map(([key, op]) => ({
                label: op.label,
                value: key,
                description: op.description,
              }))}
            />
          </FormField>

          {opConfig && (
            <ColumnLayout columns={2}>
              {opConfig.fields.map((field) => (
                <FormField
                  key={field.key}
                  label={field.label}
                  constraintText={field.required ? 'Required' : 'Optional'}
                >
                  <Input
                    value={fieldValues[field.key] ?? field.defaultValue ?? ''}
                    onChange={({ detail }) => handleFieldChange(field.key, detail.value)}
                    placeholder={field.placeholder}
                  />
                </FormField>
              ))}
            </ColumnLayout>
          )}

          <Box float="right">
            <Button variant="primary" loading={loading} onClick={handleExecute}>
              Execute
            </Button>
          </Box>
        </SpaceBetween>
      </Container>

      {/* Result */}
      {(result || error) && (
        <Container
          header={
            <Header variant="h2">
              Result{' '}
              {result && <StatusIndicator type="success">OK</StatusIndicator>}
              {error && <StatusIndicator type="error">Error</StatusIndicator>}
              {elapsed !== null && (
                <Box display="inline-block" margin={{ left: 's' }} fontSize="body-s" color="text-body-secondary">
                  {elapsed}ms
                </Box>
              )}
            </Header>
          }
        >
          <pre
            style={{
              background: error ? '#2d1b1b' : '#0d1117',
              color: error ? '#f85149' : '#c9d1d9',
              padding: '16px',
              borderRadius: '8px',
              fontSize: '13px',
              lineHeight: '1.5',
              overflow: 'auto',
              maxHeight: '500px',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {error || result}
          </pre>
        </Container>
      )}
    </SpaceBetween>
  );
}
